import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertCircle, Shield } from 'lucide-react';
import { Button, Input } from '@puglieseweb/shared-react-ui';
import {
  AuthShell,
  AuthFooterLink,
  AdmissionDeniedNotice,
  InAppSignInResume,
  nextFromSearch,
} from '@puglieseweb/shared-react-auth';
import { Spinner } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthHeader } from '@/components/layout/AuthHeader';
import { APP_ORIGIN, signsInThroughAppHost } from '@/utils/subdomain';
import { POST_LOGIN_REDIRECT_KEY, leaveFor } from '@/lib/returnTo';

/**
 * Log in — SystemDox's own page (it carries the cross-surface redirect guard
 * below, which the shared page has no notion of) rendered inside the shared
 * AuthShell, so it matches the sign-up page exactly.
 *
 * The front door is the hosted UI when App.tsx configures the domain
 * ([ADR-061] §8, C2): one "Continue" button, a top-level redirect to the
 * pool's managed login (Google/Apple live there), and straight back if a
 * session from a sibling product already exists. `?front-door=in-app` is the
 * escape hatch to the form below; it bypasses nothing — admission is
 * enforced at the token mint. A refused sign-in comes back as an unusable
 * token and is explained by `AdmissionDeniedNotice` on either door.
 *
 * A step-up (ADR-061 Amendment 18) always lands on the in-app door: the
 * factory's `SignInAgainButton` opens `/login?front-door=in-app&next=…`, and
 * `InAppSignInResume` (around the page) sends a pending one there if its
 * sign-out came back to plain `/login`. A workspace host has no form of its
 * own: it hands off to the app host's (`appHostLoginUrl`).
 */

/**
 * The cross-surface `?redirect=` is an absolute URL, which the shared
 * library's deep-link stash refuses on purpose (same-origin paths only), so
 * this page keeps it here across the hosted round trip and `PostLoginRedirect`
 * — mounted above the routes — honours it on the first authenticated render,
 * re-validated by `safeRedirect` (the key: lib/returnTo).
 */
export function PostLoginRedirect() {
  const { isAuthenticated } = useAuthContext();
  useEffect(() => {
    if (!isAuthenticated) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (raw !== null) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    } catch {
      return;
    }
    const target = safeRedirect(raw);
    if (target) leaveFor(target);
  }, [isAuthenticated]);
  return null;
}

/**
 * Where a workspace host's `/login` hands off to: the app host's `/login`,
 * with `redirect` coming back to the page on this host (utils/subdomain ›
 * signsInThroughAppHost, #1400). `front-door=in-app` and `next` go along
 * unchanged, so a step-up's *Sign in again* opens the app host's in-app form
 * with password and code, never its managed-login *Continue*, which cannot
 * prove a second factor (ADR-061 Amendment 18 §1).
 *
 * `back` is the cross-surface `?redirect=` this page was given, else `next`
 * (or `from`) on this origin.
 */
export function appHostLoginUrl(search: string, origin: string, from = '/'): string {
  const params = new URLSearchParams(search);
  const next = nextFromSearch(search);
  const back = safeRedirect(params.get('redirect')) ?? `${origin}${next ?? from}`;
  const query = [
    ...(params.get('front-door') === 'in-app' ? ['front-door=in-app'] : []),
    ...(next ? [`next=${encodeURIComponent(next)}`] : []),
    `redirect=${encodeURIComponent(back)}`,
  ];
  return `${APP_ORIGIN}/login?${query.join('&')}`;
}

/**
 * Validate a cross-surface post-login redirect (e.g. from the docs portal's
 * auth gate: /login?redirect=https://{tenant}.systemdox.com/...). Only absolute
 * https URLs on systemdox.com (or a subdomain) are allowed — anything else is
 * rejected to prevent an open redirect. Returns the href, or null.
 */
export function safeRedirect(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  if (url.hostname !== 'systemdox.com' && !url.hostname.endsWith('.systemdox.com')) return null;
  return url.href;
}

export default function LoginPage() {
  return (
    <InAppSignInResume loginPath="/login">
      <LoginScreen />
    </InAppSignInResume>
  );
}

function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login, confirmMFA, mfaChallenge, mfaDestination, isAuthenticated, isLoading, error: authError,
    frontDoor, ssoSignIn, admissionDenied, admissionReason, logout,
  } = useAuthContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `?next=` first: a step-up's Sign in again carries only that (no state).
  const from = nextFromSearch(location.search) ?? (location.state as { from?: string } | null)?.from ?? '/';
  // Cross-surface return target (docs portal auth gate). A hard navigation is
  // required — it's a different subdomain, so react-router can't route there.
  const externalRedirect = safeRedirect(new URLSearchParams(location.search).get('redirect'));
  const inAppFallback = new URLSearchParams(location.search).get('front-door') === 'in-app';
  // A workspace host is not a registered sign-in address: sign in on the app
  // host and come back here (utils/subdomain › signsInThroughAppHost). The
  // in-app door goes at once, *Continue* when pressed.
  const viaAppHost = signsInThroughAppHost();
  const inAppHandOff = viaAppHost && inAppFallback;

  useEffect(() => {
    if (inAppHandOff) leaveFor(appHostLoginUrl(location.search, window.location.origin, from));
  }, [inAppHandOff, location.search, from]);

  const handleContinue = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    if (viaAppHost) {
      leaveFor(appHostLoginUrl(location.search, window.location.origin, from));
      return;
    }
    try {
      if (externalRedirect) sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, externalRedirect);
      await ssoSignIn({ next: from !== '/' ? from : null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setIsSubmitting(false);
    }
  }, [viaAppHost, externalRedirect, ssoSignIn, from, location.search]);

  const goAfterAuth = useCallback(() => {
    if (externalRedirect) leaveFor(externalRedirect);
    else navigate(from, { replace: true });
  }, [externalRedirect, navigate, from]);

  useEffect(() => {
    if (isAuthenticated && !mfaChallenge) {
      goAfterAuth();
    }
  }, [isAuthenticated, mfaChallenge, goAfterAuth]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, login]);

  const handleMFASubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await confirmMFA(mfaCode);
      goAfterAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA verification failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [mfaCode, confirmMFA, goAfterAuth]);

  const displayError = error || authError;

  if (isLoading || inAppHandOff) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AuthHeader />
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  const isMfaStage =
    mfaChallenge === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
    mfaChallenge === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE';

  if (frontDoor === 'hosted' && !inAppFallback && !isMfaStage) {
    return (
      <AuthShell
        brandName="SystemDox"
        header={<AuthHeader />}
        logo={null}
        title="Log in"
        subtitle="Sign in to your SystemDox workspace."
        legal={{
          privacyUrl: 'https://www.systemdox.com/privacy',
          termsUrl: 'https://www.systemdox.com/terms',
        }}
        footer={<AuthFooterLink prompt="Don't have an account?" to="/signup" label="Sign up" />}
      >
        {displayError && (
          <div
            role="alert"
            className="mb-5 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <p>{displayError}</p>
          </div>
        )}
        {admissionDenied ? (
          <AdmissionDeniedNotice reason={admissionReason} onSignOut={logout} />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Continue to SystemDox with your account. If you are already signed in to another of our products, you will come straight back.
            </p>
            <Button
              type="button"
              variant="brand"
              fullWidth
              className="h-11 shadow-none hover:scale-100"
              disabled={isSubmitting}
              onClick={() => { void handleContinue(); }}
            >
              {isSubmitting ? 'Redirecting...' : 'Continue'}
            </Button>
          </div>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      brandName="SystemDox"
      header={<AuthHeader />}
      logo={null}
      title={isMfaStage ? 'Two-factor authentication' : 'Log in'}
      subtitle={isMfaStage ? undefined : 'Sign in to your SystemDox workspace.'}
      legal={{
        privacyUrl: 'https://www.systemdox.com/privacy',
        termsUrl: 'https://www.systemdox.com/terms',
      }}
      footer={
        isMfaStage ? null : <AuthFooterLink prompt="Don't have an account?" to="/signup" label="Sign up" />
      }
    >
      {displayError && (
        <div
          role="alert"
          className="mb-5 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <p>{displayError}</p>
        </div>
      )}

      {admissionDenied && (
        <AdmissionDeniedNotice reason={admissionReason} onSignOut={logout} className="mb-5" />
      )}

      {isMfaStage ? (
        <form onSubmit={handleMFASubmit} className="space-y-5">
          <div className="flex items-start gap-3">
            <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
            <p id="mfa-code-hint" className="text-sm text-muted-foreground">
              {mfaChallenge === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
                ? `Enter the 6-digit code we emailed to ${mfaDestination ?? 'your email address'}.`
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
          </div>
          <Input
            id="mfa-code"
            aria-label="Verification code"
            aria-describedby="mfa-code-hint"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={mfaCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMfaCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            disabled={isSubmitting}
            autoFocus
            className="text-center text-2xl tracking-widest"
          />
          <Button
            type="submit"
            variant="brand"
            fullWidth
            className="h-11 shadow-none hover:scale-100"
            disabled={isSubmitting || mfaCode.length !== 6}
          >
            {isSubmitting ? 'Verifying...' : 'Verify code'}
          </Button>
          <div className="text-center">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Back to login
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
              Work email
            </label>
            <Input
              type="email"
              id="email"
              inputSize="sm"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              maxLength={254}
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
              Password
            </label>
            <Input
              type="password"
              id="password"
              inputSize="sm"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <Button
            type="submit"
            variant="brand"
            fullWidth
            className="h-11 shadow-none hover:scale-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
